import type { ExpressionSpecification, MapMouseEvent, PaddingOptions } from 'mapbox-gl';
import { type ComponentProps, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type MapRef, Source } from 'react-map-gl/mapbox';
import type MapLocation from '@alltrails/amplitude/enums/MapLocation';
import logExploreMapPinSelected from '@alltrails/amplitude/events/logExploreMapPinSelected';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import { useClientNavigationPushState, useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type { TrailheadInView } from '@alltrails/types/Trailhead';
import useHideBasemapTrailheadLayer from '../../hooks/useHideBasemapTrailheadLayer';
import useMap from '../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateActiveTrails, updateCursor } from '../../redux/reducer';
import { setHoveredTrailheadId, setTrailheadsInView } from '../../redux/trailhead';
import { expandedTrailheadIds, trailheadsFromFeatures, trailheadsMinZoom, visibleMapQueryBox } from '../../utils/countTrailheadsInView';
import { featureLayers, trailheadsOverlayCollapsedActiveId, trailheadsOverlayCollapsedId, trailheadsOverlayLayerId } from '../../utils/layers';
import pinZoomPopularityMap from '../../utils/pinZoomPopularityMap';
import { getTrailheadPinStyle, type TrailheadsExploreVariant } from '../../utils/trailheadPinVariant';
import { buildTrailheadResultExpressions, trailsByTrailheadId } from '../../utils/trailheadTrailResults';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import TrailheadVectorPillPinLayer from '../TrailheadVectorPillPinLayer/TrailheadVectorPillPinLayer';
import TrailheadVectorPinLayer from '../TrailheadVectorPinLayer/TrailheadVectorPinLayer';
import ActiveTrailPolylines from '../TrailResults/ActiveTrailPolylines';
import ActiveTrailPopup from '../TrailResults/ActiveTrailPopup';

type TrailheadVectorOverlayProps = {
  activeTrailheadId?: number; // The trailhead the page is open on, drawn as an active pin whether or not it is hovered
  handleFavoriteClick?: (trailId: number) => void; // Used to handle interacting with trail card heart button
  mapLocation: MapLocation | string; // Accept string for legacy PageStrings support
  hasFiltersApplied?: boolean; // Trailheads with no trail in the filtered results stay on the map as secondary pins
  mapPadding?: PaddingOptions;
  pinVariant?: TrailheadsExploreVariant; // Which pin art and label style to draw, from the trailheads flag
} & Required<Pick<ComponentProps<typeof ActiveTrailPopup>, 'cardLocation' | 'detailedCardLocation'>>;

const apiKey = process.env.NEXT_PUBLIC_API_KEY!;
const sourceId = 'trailheads-overlay';
const sourceLayer = 'trailheads';
const pinLayerIds = [trailheadsOverlayLayerId, trailheadsOverlayCollapsedId, trailheadsOverlayCollapsedActiveId];
const inactiveExpression: ExpressionSpecification = ['literal', false];
const collapseEveryPinExpression: ExpressionSpecification = ['literal', true];
const hasTrailsFilter: ExpressionSpecification = ['>', ['coalesce', ['get', 'trail_count'], 0], 0];

// Avoid rendering too many polylines or trail cards at once for now for performance reasons
// There are trailheads on alpha with over 400 trails
const maxPolylines = 10;

// A hovered pin swaps between the collapsed and collapsed-active layers, and both Mapbox and the
// interactions provider report that swap as a leave on the layer the pin came from. Reading what is
// actually under the cursor tells the swap apart from the cursor leaving the pin, and keeps a pin
// that another layer now covers from staying hovered.
const isTrailheadUnderCursor = (map: MapRef, point: MapMouseEvent['point']) => {
  const renderedLayerIds = featureLayers.filter(layerId => map.getLayer(layerId));
  if (!renderedLayerIds.length) {
    return false;
  }
  const topLayerId = map.queryRenderedFeatures(point, { layers: renderedLayerIds })[0]?.layer?.id;
  return topLayerId !== undefined && pinLayerIds.includes(topLayerId);
};

const sameTrailheadIds = (current: TrailheadInView['id'][], next: TrailheadInView['id'][]) =>
  current.length === next.length && current.every((id, index) => id === next[index]);

export default function TrailheadVectorOverlay({
  activeTrailheadId,
  cardLocation,
  detailedCardLocation,
  hasFiltersApplied = false,
  mapLocation,
  mapPadding,
  pinVariant
}: TrailheadVectorOverlayProps) {
  const publicBaseUrl = usePublicBaseUrl();
  const map = useMap();
  const searchParams = useClientNavigationSearchParams();
  const pushState = useClientNavigationPushState();
  const languageRegionCode = useLanguageRegionCode();
  useHideBasemapTrailheadLayer(map);
  const dispatch = useAppDispatch();
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const isMobile = useIsMobileSizedScreen();
  const hoveredTrailheadId = useAppSelector(state => state.trailhead.hoveredTrailheadId);
  const { mapZoomBucket, trailResults } = useAppSelector(state => ({
    mapZoomBucket: state.map.mapZoomBucket,
    trailResults: state.map.trailResults
  }));
  const [expandedIds, setExpandedIds] = useState<TrailheadInView['id'][]>([]);
  const trailsByTrailhead = useMemo(() => trailsByTrailheadId(trailResults), [trailResults]);
  // Click and hover read the latest search through this ref. Depending on it directly recreates
  // those handlers on every refresh, and the interaction cleanup then runs while the pointer can
  // still be sitting on a pin.
  const trailsByTrailheadRef = useRef(trailsByTrailhead);
  const tiles = [`${publicBaseUrl}/wmts/overlay/trailheads/{z}/{x}/{y}?key=${apiKey}`];
  const { maxDesktopRegularPins, maxMobileRegularPins } = pinZoomPopularityMap(mapZoomBucket);
  const maxExpandedPins = isMobile ? maxMobileRegularPins : maxDesktopRegularPins;

  const analyticsInfo = useMemo(
    () => (detailedCardLocation && mapLocation ? { detailedCardLocation, page: mapLocation } : undefined),
    [detailedCardLocation, mapLocation]
  );
  const { isFavorite: isSavedUtil, isComplete: isCompletedUtil, listModal, signUpModal } = useReduxListItems(undefined, undefined, analyticsInfo);
  const isTrailSaved = useCallback((trailId: number) => isSavedUtil('trail', trailId), [isSavedUtil]);
  const isTrailCompleted = useCallback((trailId: number) => isCompletedUtil('trail', trailId), [isCompletedUtil]);

  // The pin layers draw whatever this matches as the active pin, so the open trailhead is matched alongside the hovered one.
  const isHoveredExpression = useMemo<ExpressionSpecification>(() => {
    const isHovered: ExpressionSpecification = ['==', ['id'], hoveredTrailheadId ?? -1];
    return activeTrailheadId === undefined ? isHovered : ['any', isHovered, ['==', ['id'], activeTrailheadId]];
  }, [activeTrailheadId, hoveredTrailheadId]);
  // On a trailhead page every pin but the open trailhead's is drawn faintly.
  const isDimmedExpression = useMemo<ExpressionSpecification | undefined>(
    () => (activeTrailheadId === undefined ? undefined : ['!=', ['id'], activeTrailheadId]),
    [activeTrailheadId]
  );
  const { isClosedExpression, isCompletedExpression, isSavedExpression, matchedTrailheadsFilter, popularityExpression, trailCountExpression } =
    useMemo(() => buildTrailheadResultExpressions(trailResults, { isTrailCompleted, isTrailSaved }), [isTrailCompleted, isTrailSaved, trailResults]);
  // With filters applied, a trailhead with no matching trail stays on the map as a secondary pin instead of dropping out.
  // Without them the results are what is drawn, so a trailhead outside them is one the search has not reached.
  // The results are undefined until the first search lands (a zero-result search leaves an empty object), so before that,
  // such as the first load or a shared link that already has filters, every pin is held back rather than drawn gray and
  // then flipped to primary.
  const showsSecondaryPins = hasFiltersApplied && trailResults !== undefined;
  const pinFilter = useMemo<ExpressionSpecification>(
    () => (showsSecondaryPins ? hasTrailsFilter : ['all', hasTrailsFilter, matchedTrailheadsFilter]),
    [matchedTrailheadsFilter, showsSecondaryPins]
  );
  const isSecondaryExpression = useMemo<ExpressionSpecification | undefined>(
    () => (showsSecondaryPins ? ['!', matchedTrailheadsFilter] : undefined),
    [matchedTrailheadsFilter, showsSecondaryPins]
  );

  // Mirrors what trailCountExpression shows on the label, so ranking can't send an expanded slot to a
  // trailhead with more total trails but fewer that actually matched the current search. Read through a
  // ref rather than a dependency of the idle-driven effect below, so a new search doesn't tear down and
  // re-subscribe that map listener.
  const matchedTrailCountById = useMemo(() => {
    const counts: Record<string, number> = {};
    trailsByTrailhead.forEach((trails, trailheadId) => {
      counts[String(trailheadId)] = trails.length;
    });
    return counts;
  }, [trailsByTrailhead]);
  const matchedTrailCountByIdRef = useRef(matchedTrailCountById);

  // The click handler reads its navigation inputs through this ref. pushState is a new function every
  // render and searchParams changes on every navigation, so depending on them recreates the handlers and
  // the registration cleanup then runs while the pointer can still be sitting on a pin.
  const navigateToTrailheadRef = useRef<(trailheadId: string | number) => void>(() => {});
  useEffect(() => {
    navigateToTrailheadRef.current = trailheadId => {
      pushState(wrapUrlSafe(`/explore/trailhead/${trailheadId}?${searchParams.toString()}`, languageRegionCode));
    };
  }, [languageRegionCode, pushState, searchParams]);

  useEffect(() => {
    matchedTrailCountByIdRef.current = matchedTrailCountById;
  }, [matchedTrailCountById]);

  useEffect(() => {
    trailsByTrailheadRef.current = trailsByTrailhead;
  }, [trailsByTrailhead]);

  // The ranking can only read pins the map already drew, so a trailhead that just came into view has
  // to render once before it can win an expanded pin. It renders as a dot until then, so zooming in
  // can't flash a screen of full pins that the next idle pass immediately takes back. Zoomed all the
  // way in there is no cap, so nothing collapses.
  const isCollapsedExpression = useMemo<ExpressionSpecification>(() => {
    if (maxExpandedPins === undefined) {
      return inactiveExpression;
    }
    return expandedIds.length ? ['!', ['in', ['id'], ['literal', expandedIds]]] : collapseEveryPinExpression;
  }, [expandedIds, maxExpandedPins]);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const updateTrailheadsInView = () => {
      // Collapsed pins have to stay in the query so a trailhead that collapsed this pass can still
      // win an expanded pin on the next one.
      const renderedLayerIds = pinLayerIds.filter(layerId => map.getLayer(layerId));
      if (!renderedLayerIds.length || !mapPadding) {
        dispatch(setTrailheadsInView([]));
        return;
      }
      const queryBox = visibleMapQueryBox(map.getCanvas(), mapPadding);
      const features = queryBox
        ? map.queryRenderedFeatures(queryBox, { layers: renderedLayerIds })
        : map.queryRenderedFeatures({ layers: renderedLayerIds });
      const trailheads = trailheadsFromFeatures(features);
      dispatch(setTrailheadsInView(trailheads));
      const rankedTrailheads = trailheads.map(trailhead => {
        const matchedTrailCount = matchedTrailCountByIdRef.current[String(trailhead.id)];
        return matchedTrailCount === undefined ? trailhead : { ...trailhead, trailCount: matchedTrailCount };
      });
      const nextExpandedIds = maxExpandedPins === undefined ? [] : expandedTrailheadIds(rankedTrailheads, maxExpandedPins);
      setExpandedIds(currentIds => (sameTrailheadIds(currentIds, nextExpandedIds) ? currentIds : nextExpandedIds));
    };

    updateTrailheadsInView();
    map.on('idle', updateTrailheadsInView);
    return () => {
      map.off('idle', updateTrailheadsInView);
      dispatch(setTrailheadsInView([]));
    };
  }, [dispatch, map, maxExpandedPins, mapPadding]);

  const openTrailhead = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature || feature.geometry.type !== 'Point' || feature.id === undefined) {
        return;
      }
      const trailheadId = Number(feature.id);
      const trails = Number.isSafeInteger(trailheadId) ? (trailsByTrailheadRef.current.get(trailheadId) ?? []) : [];
      if (!trails.length) {
        return;
      }

      // The trailhead page draws its own trails, so the hovered pin and its hover polyline are done with.
      dispatch(setHoveredTrailheadId(undefined));
      dispatch(updateActiveTrails(undefined));
      if (cardLocation) {
        logExploreMapPinSelected({
          action_taken: 'clicked on explore map',
          pin_type: 'trailhead',
          trail_id: trails[0].ID,
          map_location: mapLocation as MapLocation | undefined
        });
      }
      navigateToTrailheadRef.current(feature.id);
    },
    [cardLocation, dispatch, mapLocation]
  );

  // mousemove rather than mouseenter so moving between two adjacent pins still reports the new one.
  const hoverTrailhead = useCallback(
    (event: MapMouseEvent) => {
      dispatch(updateCursor('pointer'));
      const feature = event.features?.[0];
      dispatch(setHoveredTrailheadId(feature?.id));
      // On a trailhead page the open trailhead's trails are the ones drawn, so another pin only shows it can be clicked.
      if (!feature || activeTrailheadId !== undefined) {
        return;
      }
      const trailheadId = Number(feature.id);
      const trails = Number.isSafeInteger(trailheadId) ? (trailsByTrailheadRef.current.get(trailheadId) ?? []) : [];
      dispatch(updateActiveTrails(trails.length ? trails : undefined));
    },
    [activeTrailheadId, dispatch]
  );

  const leaveTrailhead = useCallback(
    (event?: MapMouseEvent) => {
      if (map && event?.point && isTrailheadUnderCursor(map, event.point)) {
        return;
      }
      dispatch(updateCursor(undefined));
      dispatch(setHoveredTrailheadId(undefined));
      dispatch(updateActiveTrails(undefined));
    },
    [dispatch, map]
  );

  useEffect(() => {
    pinLayerIds.forEach(layerId => {
      registerEventHandler(layerId, 'click', openTrailhead);
      // Touch fires a synthetic mousemove on tap with no leave to follow, which would strand a
      // tapped pin in the hovered state.
      if (!isMobile) {
        registerEventHandler(layerId, 'mousemove', hoverTrailhead);
        registerEventHandler(layerId, 'mouseleave', leaveTrailhead);
      }
    });
    return () => {
      pinLayerIds.forEach(layerId => {
        unregisterEventHandler(layerId, 'click', openTrailhead);
        unregisterEventHandler(layerId, 'mousemove', hoverTrailhead);
        unregisterEventHandler(layerId, 'mouseleave', leaveTrailhead);
      });
    };
  }, [hoverTrailhead, isMobile, leaveTrailhead, openTrailhead, registerEventHandler, unregisterEventHandler]);

  // Re-binding the handlers above is not a leave, so only unmount resets the hover, the cursor and the
  // hover polyline. A rebind that reset them would clear a hover the pointer is still sitting on.
  useEffect(() => {
    return () => {
      dispatch(updateCursor(undefined));
      dispatch(setHoveredTrailheadId(undefined));
      dispatch(updateActiveTrails(undefined));
    };
  }, [dispatch]);

  return (
    <>
      <Source id={sourceId} minzoom={trailheadsMinZoom} tiles={tiles} type="vector">
        {getTrailheadPinStyle(pinVariant).shape === 'pill' ? (
          <TrailheadVectorPillPinLayer
            collapsedLayerId={trailheadsOverlayCollapsedId}
            filter={pinFilter}
            isClosedExpression={isClosedExpression}
            isCollapsedExpression={isCollapsedExpression}
            isCompletedExpression={isCompletedExpression}
            isDimmedExpression={isDimmedExpression}
            isHoveredExpression={isHoveredExpression}
            isSavedExpression={isSavedExpression}
            isSecondaryExpression={isSecondaryExpression}
            layerId={trailheadsOverlayLayerId}
            pinVariant={pinVariant}
            popularityExpression={popularityExpression}
            sourceId={sourceId}
            sourceLayer={sourceLayer}
            trailsByTrailhead={trailsByTrailhead}
          />
        ) : (
          <TrailheadVectorPinLayer
            collapsedActiveLayerId={trailheadsOverlayCollapsedActiveId}
            collapsedLayerId={trailheadsOverlayCollapsedId}
            filter={pinFilter}
            isClosedExpression={isClosedExpression}
            isCollapsedExpression={isCollapsedExpression}
            isCompletedExpression={isCompletedExpression}
            isDimmedExpression={isDimmedExpression}
            isHoveredExpression={isHoveredExpression}
            isSavedExpression={isSavedExpression}
            isSecondaryExpression={isSecondaryExpression}
            layerId={trailheadsOverlayLayerId}
            pinVariant={pinVariant}
            popularityExpression={popularityExpression}
            sourceId={sourceId}
            sourceLayer={sourceLayer}
            trailCountExpression={trailCountExpression}
          />
        )}
      </Source>
      <ActiveTrailPolylines maxPolylines={maxPolylines} />
      {listModal}
      {signUpModal}
    </>
  );
}
